import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/assets-manager`;

const AssetContext = createContext(null);

export const useAsset = () => {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error('useAsset must be used within an AssetProvider');
  }
  return context;
};

export const AssetProvider = ({ children, token, role }) => {
  // Silent access-token refresh — see useAuthRefresh for the rationale.
  useAuthRefresh(token, { backendUrl: BACKEND_URL, apiPrefix: '/api/assets-manager' });

  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 0
  });
  const [lovs, setLovs] = useState({
    asset_types: [],
    asset_statuses: [],
    warranty_statuses: [],
    employee_statuses: []
  });

  // Create axios instance with auth token
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
    return instance;
  }, [token]);

  const isReady = Boolean(token);

  // ================= DASHBOARD =================
  const getDashboardStats = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      return null;
    }
  }, [api]);

  const getStatusDistribution = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/status-distribution');
      return response.data;
    } catch (err) {
      console.error('Failed to fetch status distribution:', err);
      return [];
    }
  }, [api]);

  // ================= LOVs =================
  const fetchLOVs = useCallback(async () => {
    try {
      const response = await api.get('/lovs');
      const data = response.data;
      setLovs({
        asset_types: (data.asset_types || []).filter(i => i.is_active).map(i => i.value),
        asset_statuses: (data.asset_statuses || []).filter(i => i.is_active).map(i => i.value),
        warranty_statuses: (data.warranty_statuses || []).filter(i => i.is_active).map(i => i.value),
        employee_statuses: (data.employee_statuses || []).filter(i => i.is_active).map(i => i.value)
      });
      return data;
    } catch (err) {
      console.error('Failed to fetch LOVs:', err);
      return {};
    }
  }, [api]);

  const getLOVsRaw = useCallback(async () => {
    try {
      const response = await api.get('/lovs');
      return response.data;
    } catch (err) {
      console.error('Failed to fetch LOVs:', err);
      return {};
    }
  }, [api]);

  const addLOVItem = useCallback(async (category, value) => {
    try {
      await api.post('/lovs', { category, value, is_active: true });
      await fetchLOVs();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to add LOV item' };
    }
  }, [api, fetchLOVs]);

  const updateLOVItem = useCallback(async (lovId, data) => {
    try {
      await api.put(`/lovs/${lovId}`, data);
      await fetchLOVs();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update LOV item' };
    }
  }, [api, fetchLOVs]);

  const deleteLOVItem = useCallback(async (lovId) => {
    try {
      await api.delete(`/lovs/${lovId}`);
      await fetchLOVs();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete LOV item' };
    }
  }, [api, fetchLOVs]);

  // ================= ASSETS =================
  const fetchAssets = useCallback(async (filters = {}, page = 1, pageSize = 50) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.asset_type) params.append('asset_type', filters.asset_type);
      if (filters.age_filter) params.append('age_filter', filters.age_filter);
      if (filters.warranty_filter) params.append('warranty_filter', filters.warranty_filter);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);

      const response = await api.get(`/assets?${params.toString()}`);
      
      if (response.data.data && response.data.pagination) {
        setAssets(response.data.data);
        setPagination(response.data.pagination);
        return response.data;
      } else {
        setAssets(response.data);
        return { data: response.data, pagination: { page: 1, page_size: response.data.length, total: response.data.length, total_pages: 1 } };
      }
    } catch (err) {
      console.error('Failed to fetch assets:', err);
      return { data: [], pagination: { page: 1, page_size: 50, total: 0, total_pages: 0 } };
    } finally {
      setLoading(false);
    }
  }, [api]);

  const getAsset = useCallback(async (assetId) => {
    try {
      const response = await api.get(`/assets/${assetId}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch asset:', err);
      return null;
    }
  }, [api]);

  const createAsset = useCallback(async (assetData) => {
    try {
      const response = await api.post('/assets', assetData);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create asset' };
    }
  }, [api]);

  const updateAsset = useCallback(async (assetId, assetData) => {
    try {
      await api.put(`/assets/${assetId}`, assetData);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update asset' };
    }
  }, [api]);

  const deleteAsset = useCallback(async (assetId) => {
    try {
      await api.delete(`/assets/${assetId}`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete asset' };
    }
  }, [api]);

  const bulkDeleteAssets = useCallback(async (assetIds) => {
    try {
      const response = await api.post('/assets/bulk-delete', assetIds);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete assets' };
    }
  }, [api]);

  const allocateAsset = useCallback(async (assetId, employeeId) => {
    try {
      await api.post('/assets/allocate', { asset_id: assetId, employee_id: employeeId });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to allocate asset' };
    }
  }, [api]);

  const unallocateAsset = useCallback(async (assetId) => {
    try {
      await api.post(`/assets/${assetId}/unallocate`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to unallocate asset' };
    }
  }, [api]);

  const getAssetHistory = useCallback(async (assetId) => {
    try {
      const response = await api.get(`/assets/${assetId}/history`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch asset history:', err);
      return [];
    }
  }, [api]);

  const getAssetAllocationHistory = useCallback(async (assetId) => {
    try {
      const response = await api.get(`/assets/${assetId}/allocation-history`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch asset allocation history:', err);
      return [];
    }
  }, [api]);

  // ================= ASSET INVOICES =================
  const getAssetInvoice = useCallback(async (assetId) => {
    try {
      const response = await api.get(`/assets/${assetId}/invoice`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch asset invoice:', err);
      return null;
    }
  }, [api]);

  const listInvoices = useCallback(async (search = '') => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await api.get(`/invoices${params}`);
      return response.data || [];
    } catch (err) {
      console.error('Failed to list invoices:', err);
      return [];
    }
  }, [api]);

  const uploadAssetInvoice = useCallback(async (assetId, invoiceNo, file, replaceIfExists = false) => {
    try {
      const formData = new FormData();
      formData.append('invoice_no', invoiceNo);
      formData.append('file', file);
      formData.append('replace_if_exists', replaceIfExists ? 'true' : 'false');
      const response = await api.post(`/assets/${assetId}/invoice/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return {
        success: false,
        status: err.response?.status,
        error: err.response?.data?.detail || 'Failed to upload invoice'
      };
    }
  }, [api]);

  const linkAssetInvoice = useCallback(async (assetId, invoiceNo) => {
    try {
      const response = await api.post(`/assets/${assetId}/invoice/link`, { invoice_no: invoiceNo });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to link invoice' };
    }
  }, [api]);

  const unlinkAssetInvoice = useCallback(async (assetId) => {
    try {
      const response = await api.delete(`/assets/${assetId}/invoice`);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to unlink invoice' };
    }
  }, [api]);

  const downloadAssetInvoice = useCallback(async (assetId) => {
    try {
      const response = await api.get(`/assets/${assetId}/invoice/download`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) {
        // Fallback to download if popup blocked
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `invoice_${assetId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Failed to open invoice file' };
    }
  }, [api]);

  const downloadAssetTemplate = useCallback(async () => {
    try {
      const response = await api.get('/assets/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'assets_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Failed to download template' };
    }
  }, [api]);

  const importAssets = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/assets/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to import assets' };
    }
  }, [api]);

  const exportAssets = useCallback(async () => {
    try {
      const response = await api.get('/assets/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assets_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Failed to export assets' };
    }
  }, [api]);

  // ================= EMPLOYEES =================
  const fetchEmployees = useCallback(async (filters = {}, page = 1, pageSize = 50) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);

      const response = await api.get(`/employees?${params.toString()}`);
      
      if (response.data.data && response.data.pagination) {
        setEmployees(response.data.data);
        return response.data;
      } else {
        setEmployees(response.data);
        return { data: response.data, pagination: { page: 1, page_size: response.data.length, total: response.data.length, total_pages: 1 } };
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      return { data: [], pagination: { page: 1, page_size: 50, total: 0, total_pages: 0 } };
    }
  }, [api]);

  const getEmployee = useCallback(async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch employee:', err);
      return null;
    }
  }, [api]);

  const createEmployee = useCallback(async (employeeData) => {
    try {
      const response = await api.post('/employees', employeeData);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create employee' };
    }
  }, [api]);

  const updateEmployee = useCallback(async (employeeId, employeeData) => {
    try {
      await api.put(`/employees/${employeeId}`, employeeData);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update employee' };
    }
  }, [api]);

  const deleteEmployee = useCallback(async (employeeId) => {
    try {
      await api.delete(`/employees/${employeeId}`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete employee' };
    }
  }, [api]);

  const getEmployeeAllocationHistory = useCallback(async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}/allocation-history`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch employee allocation history:', err);
      return [];
    }
  }, [api]);

  const getEmployeeAssetAllocationHistory = useCallback(async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}/asset-allocation-history`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch employee asset allocation history:', err);
      return [];
    }
  }, [api]);

  const getEmployeeUpdateHistory = useCallback(async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}/update-history`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch employee update history:', err);
      return [];
    }
  }, [api]);

  const revealAssetBitlockerKey = useCallback(async (assetId) => {
    try {
      const response = await api.get(`/assets/${assetId}/bitlocker-key`);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to reveal key' };
    }
  }, [api]);

  const getEmployeeAllocatedAssets = useCallback(async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}/allocated-assets`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch allocated assets:', err);
      return { employee: null, assets: [] };
    }
  }, [api]);

  const allocateEmployeeToProject = useCallback(async (employeeId, projectId) => {
    try {
      await api.post(`/employees/${employeeId}/project?project_id=${projectId}`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to allocate to project' };
    }
  }, [api]);

  const unallocateEmployeeFromProject = useCallback(async (employeeId) => {
    try {
      await api.delete(`/employees/${employeeId}/project`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to unallocate from project' };
    }
  }, [api]);

  const downloadEmployeeTemplate = useCallback(async () => {
    try {
      const response = await api.get('/employees/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Failed to download template' };
    }
  }, [api]);

  const importEmployees = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/employees/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to import employees' };
    }
  }, [api]);

  const exportEmployees = useCallback(async () => {
    try {
      const response = await api.get('/employees/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employees_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Failed to export employees' };
    }
  }, [api]);

  // ================= PROJECTS =================
  const fetchProjects = useCallback(async (filters = {}, page = 1, pageSize = 50) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/projects?${params.toString()}`);
      
      if (response.data.data && response.data.pagination) {
        setProjects(response.data.data);
        return response.data;
      } else {
        setProjects(response.data);
        return { data: response.data, pagination: { page: 1, page_size: response.data.length, total: response.data.length, total_pages: 1 } };
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      return { data: [], pagination: { page: 1, page_size: 50, total: 0, total_pages: 0 } };
    }
  }, [api]);

  const getProject = useCallback(async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch project:', err);
      return null;
    }
  }, [api]);

  const createProject = useCallback(async (projectData) => {
    try {
      const response = await api.post('/projects', projectData);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create project' };
    }
  }, [api]);

  const updateProject = useCallback(async (projectId, projectData) => {
    try {
      await api.put(`/projects/${projectId}`, projectData);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update project' };
    }
  }, [api]);

  const deleteProject = useCallback(async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete project' };
    }
  }, [api]);

  const allocateEmployeesToProject = useCallback(async (projectId, employeeIds) => {
    try {
      await api.post(`/projects/${projectId}/employees`, { employee_ids: employeeIds });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to allocate employees' };
    }
  }, [api]);

  // ================= AUDIT LOGS =================
  const getAuditLogs = useCallback(async (page = 1, pageSize = 50, filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (filters.action) params.append('action', filters.action);
      if (filters.username) params.append('username', filters.username);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await api.get(`/audit-logs?${params.toString()}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      return { data: [], pagination: { page: 1, page_size: 50, total: 0, total_pages: 0 } };
    }
  }, [api]);

  const value = {
    isReady,
    assets,
    employees,
    projects,
    loading,
    pagination,
    lovs,
    // Dashboard
    getDashboardStats,
    getStatusDistribution,
    // LOVs
    fetchLOVs,
    getLOVsRaw,
    addLOVItem,
    updateLOVItem,
    deleteLOVItem,
    // Assets
    fetchAssets,
    getAsset,
    createAsset,
    updateAsset,
    deleteAsset,
    bulkDeleteAssets,
    allocateAsset,
    unallocateAsset,
    getAssetHistory,
    getAssetAllocationHistory,
    getAssetInvoice,
    listInvoices,
    uploadAssetInvoice,
    linkAssetInvoice,
    unlinkAssetInvoice,
    downloadAssetInvoice,
    downloadAssetTemplate,
    importAssets,
    exportAssets,
    // Employees
    fetchEmployees,
    getEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    getEmployeeAllocationHistory,
    getEmployeeAssetAllocationHistory,
    getEmployeeUpdateHistory,
    getEmployeeAllocatedAssets,
    revealAssetBitlockerKey,
    allocateEmployeeToProject,
    unallocateEmployeeFromProject,
    downloadEmployeeTemplate,
    importEmployees,
    exportEmployees,
    // Projects
    fetchProjects,
    getProject,
    createProject,
    updateProject,
    deleteProject,
    allocateEmployeesToProject,
    // Audit Logs
    getAuditLogs,
    // Current user
    role,
    canRevealBitlocker: role === 'super_admin' || role === 'it_admin'
  };

  return (
    <AssetContext.Provider value={value}>
      {children}
    </AssetContext.Provider>
  );
};

export default AssetContext;
